import { useMutation, useQueryClient } from '@tanstack/react-query';
import { updatePrescriber } from '../../../../apis/clinics/requests';
import { useSnackbar } from 'notistack';
import { PrescribingProtocol } from '../../../../apis/users';
import { PublicProfileFormData } from '../components/tabs/ClinicPrescribers/types';
import { toPublicProfilePayload } from '../components/tabs/ClinicPrescribers/publicProfile';

interface PrescriberFormData {
  forename: string;
  surname: string;
  email: string;
  roles: string[];
  nmcNumber?: string;
  gphcNumber?: string;
  address?: string;
  postcode?: string;
  telephone?: string;
  clinic_name?: string;
  registration_body?: string;
  prescribing_protocols?: PrescribingProtocol[];
  public_profile?: PublicProfileFormData;
  show_on_clinic?: boolean;
}

interface UseUpdatePrescriberReturn {
  updatePrescriber: (prescriberId: string, data: PrescriberFormData) => Promise<void>;
  isLoading: boolean;
}

const useUpdatePrescriber = (clinicId: string): UseUpdatePrescriberReturn => {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync, isLoading } = useMutation(
    ({ prescriberId, data }: { prescriberId: string; data: PrescriberFormData }) => {
      const payload = {
        username: data.email,
        forename: data.forename,
        surname: data.surname,
        roles: data.roles,
        ...(data.telephone?.trim() && { telephone: data.telephone.trim() }),
        ...(data.address?.trim() && { address: data.address.trim() }),
        ...(data.postcode?.trim() && { postcode: data.postcode.trim() }),
        ...(data.nmcNumber?.trim() && { nmc_number: data.nmcNumber.trim() }),
        ...(data.gphcNumber?.trim() && { gphc_number: data.gphcNumber.trim() }),
        ...(data.clinic_name?.trim() && { clinic_name: data.clinic_name.trim() }),
        ...(data.registration_body?.trim() && { registration_body: data.registration_body.trim() }),
        prescribing_protocols: data.prescribing_protocols?.length ? data.prescribing_protocols : null,
        public_profile: toPublicProfilePayload(data.public_profile),
        show_on_clinic: Boolean(data.show_on_clinic)
      };

      return updatePrescriber(clinicId, prescriberId, payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['prescribers', clinicId]);
        enqueueSnackbar('Prescriber has been updated', { variant: 'success' });
      },
      onError: () => {
        enqueueSnackbar('Failed to update prescriber', { variant: 'error' });
      }
    }
  );

  return {
    updatePrescriber: (prescriberId: string, data: PrescriberFormData) => mutateAsync({ prescriberId, data }),
    isLoading
  };
};

export default useUpdatePrescriber; 
